/**
 * User Activity API Service
 * Handles fetching and managing user activity events
 */

import apiClient from './apiClient';

export interface Activity {
  id: string;
  type: 'game_started' | 'game_ended' | 'game_won' | 'game_lost' | 'player_joined' | 'player_left' | 'achievement' | 'room_created' | 'room_joined';
  message: string;
  timestamp: string;
  icon: string;
  metadata?: {
    roomId?: string;
    roomName?: string;
    gameType?: string;
    score?: number;
    achievement?: string;
    otherPlayerId?: string;
    otherPlayerName?: string;
  };
}

export interface ActivitiesResponse {
  activities: Activity[];
  count: number;
}

class UserActivityApi {
  /**
   * Get recent activities for the current user
   */
  async getRecentActivities(limit: number = 10): Promise<Activity[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: ActivitiesResponse }>(`/activities/recent?limit=${limit}`);
      
      if (response.data.success && response.data.data) {
        return response.data.data.activities;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  }

  /**
   * Log a new activity (manual)
   */
  async logActivity(
    activityType: Activity['type'],
    message: string,
    icon: string,
    metadata?: Activity['metadata']
  ): Promise<Activity | null> {
    try {
      const response = await apiClient.post<{ success: boolean; data: { activity: Activity } }>('/activities/log', {
        activityType,
        message,
        icon,
        metadata
      });
      
      if (response.data.success && response.data.data) {
        return response.data.data.activity;
      }
      
      return null;
    } catch (error) {
      console.error('Error logging activity:', error);
      return null;
    }
  }

  /**
   * Clear all activities (for testing)
   */
  async clearActivities(): Promise<boolean> {
    try {
      const response = await apiClient.delete<{ success: boolean }>('/activities/clear');
      return response.data.success;
    } catch (error) {
      console.error('Error clearing activities:', error);
      return false;
    }
  }
}

export const userActivityApi = new UserActivityApi();
export default userActivityApi;
